import { inject, Injectable } from '@angular/core';
import { filter, map } from 'rxjs';
import { OAuthService, AuthConfig, NullValidationHandler, OAuthEvent } from 'angular-oauth2-oidc';
import { AccessRight, KcUserRole, UserTypes } from '@constants/enum';
import { AppUser, TokenData } from '@defs/auth';
import { environment } from '@env';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthService = inject(OAuthService);
  private readonly authConfig = inject(AuthConfig);

  private user: AppUser | null = null;

  private readonly authIssueEvents = [
    'token_error',
    'token_validation_error',
    'silent_refresh_error',
    'session_error',
  ];

  initAuth(): Promise<unknown> {
    return new Promise((resolveFn, rejectFn) => {
      this.oauthService.events
        .pipe(
          filter((e) => this.filterOaEvents(e)),
          map(() => prepareUserByToken(decodeToken(this.oauthService.getAccessToken()))),
        )
        .subscribe({
          next: (user) => {
            this.user = user;
            resolveFn(true);
          },
          error: (err: unknown) => {
            console.error('Error during authentication:', err);
            this.oauthService.logOut();
          },
        });

      this.oauthService.configure(this.authConfig);
      this.oauthService.setStorage(localStorage);
      this.oauthService.tokenValidationHandler = new NullValidationHandler();

      this.oauthService
        .loadDiscoveryDocumentAndLogin()
        .then((isLoggedIn) => {
          if (isLoggedIn) {
            this.oauthService.setupAutomaticSilentRefresh();

            /*
              If valid tokens are present at the start time, 'token_received' event is not triggered,
              so we must prepare user by token here
            */
            this.user = prepareUserByToken(decodeToken(this.oauthService.getAccessToken()));

            resolveFn(true);
          } else {
            this.oauthService.initCodeFlow();
            rejectFn();
          }
        })
        .catch((err: unknown) => {
          console.error('Error during authentication:', err);
          this.oauthService.logOut();
          rejectFn(err);
        });
    });
  }

  getToken() {
    return this.oauthService.getAccessToken();
  }

  private filterOaEvents(e: OAuthEvent): boolean {
    if (e.type === 'token_received') {
      return true;
    }

    if (this.authIssueEvents.includes(e.type)) {
      // issue with session/token, do logout
      this.oauthService.logOut();
    }

    return false;
  }

  logOut(): void {
    this.oauthService.logOut();
  }

  hasAnyRights(ars: AccessRight[]): boolean {
    const rights = this.user?.accessRights ?? [];
    return ars.some((r) => rights.includes(r));
  }

  hasAllRights(ars: AccessRight[]): boolean {
    const rights = this.user?.accessRights ?? [];
    return ars.every((r) => rights.includes(r));
  }
}

export function prepareUserByToken(tokenData: TokenData | null): AppUser | null {
  if (!tokenData) throw new Error('Token data is required');

  let user: AppUser;
  const accessRights: AccessRight[] = [];
  const kcClientId = environment.keycloakClientId;

  if (tokenData.user_type !== UserTypes.SystemAdmin) {
    throw new Error(`User type: ${tokenData.user_type} is not allowed here`);
  }

  /*
    WARN: Here is only place to map user data to internal access rights
  */

  // default access rights
  accessRights.push(AccessRight.Customers, AccessRight.Integrations, AccessRight.Managers);

  // add here conditional mappings of other token fields to access rights

  // map KC roles to access rights
  if (Array.isArray(tokenData?.resource_access[kcClientId]?.roles)) {
    tokenData.resource_access[kcClientId].roles.forEach((role) => {
      if (role === KcUserRole.ProductAdmin) {
        accessRights.push(AccessRight.ProductAdmin);
      }
      if (role === KcUserRole.DeletePasswords) {
        accessRights.push(AccessRight.DeletePasswords);
      }

      // add more role mappings here
    });
  }

  // eslint-disable-next-line prefer-const
  user = {
    id: tokenData.sub,
    fullName: tokenData.name,
    email: tokenData.email,
    accessRights,
  };

  return user;
}

export function decodeToken(token: string | null): TokenData | null {
  if (!token) return null;
  const b64 = token.split('.')[1];
  const json = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));

  try {
    return JSON.parse(json) as TokenData;
  } catch {
    return null;
  }
}
